import { useState } from "react";
import { Send, Mail, MapPin, Loader2, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { toast } from "sonner";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      
      setSubmitted(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <SEO
        title="Contact Us"
        description="Get in touch with Mahra. Questions about outfit rentals, sizing, delivery in Melbourne? We're here to help."
        path="/contact"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Contact" }]}
      />
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      
      <Navbar />

      {/* Hero */}
      <section className="px-6 pt-12 pb-8 lg:px-16 lg:pt-20 lg:pb-12 text-center">
        <p className="text-[#C4A4A0] text-[11px] tracking-[0.3em] uppercase mb-3">Get in Touch</p>
        <h1 className="text-4xl lg:text-5xl text-[#3D2B1F] mb-4">
          Contact Us
        </h1>
        <p className="text-[#3D2B1F]/60 font-light max-w-xl mx-auto">
          Have a question or want to learn more? We'd love to hear from you.
        </p>
      </section>

      {/* Contact Section */}
      <section className="px-6 py-8 lg:px-16 lg:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-10">
            
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-[#3D2B1F] text-white p-8 h-full">
                <h2 className="text-xl mb-6">Contact Information</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#C4A4A0] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="text-white/60 text-sm mb-1">Email</p>
                      <p className="text-white/80 text-sm">Use the form to get in touch</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#C4A4A0] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="text-white/60 text-sm mb-1">Location</p>
                      <p className="text-white">Oakleigh South, VIC</p>
                      <p className="text-white/60 text-sm mt-1">Melbourne, Australia</p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-10 pt-6 border-t border-white/10">
                  <p className="text-white/60 text-sm mb-3">Follow us</p>
                  <a 
                    href="https://instagram.com/mahra.insta" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#C4A4A0] hover:text-white transition-colors"
                    data-testid="contact-instagram-link"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    @mahra.insta
                  </a>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="bg-white border border-[#282828]/10 p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-[#B8C4B8] mx-auto mb-6" strokeWidth={1} />
                  <h3 className="text-2xl text-[#3D2B1F] mb-3">Thank You!</h3>
                  <p className="text-[#3D2B1F]/60 font-light mb-6">
                    We've received your message and will get back to you shortly.
                  </p>
                  <Button 
                    onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", subject: "", message: "" }); }}
                    variant="outline"
                    className="mt-6 border-[#282828]/10"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-[#282828]/10 p-8">
                  <h2 className="text-xl text-[#3D2B1F] mb-6">Send us a Message</h2>
                  
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label className="text-[#676664]/60 text-[11px]">Your Name *</Label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jane Smith"
                          className="mt-2 border-[#282828]/10"
                          data-testid="contact-name-input"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-[#676664]/60 text-[11px]">Email Address *</Label>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jane@example.com"
                          className="mt-2 border-[#282828]/10"
                          data-testid="contact-email-input"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Subject</Label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                        className="mt-2 border-[#282828]/10"
                        data-testid="contact-subject-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Message *</Label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us how we can help..."
                        className="mt-2 border-[#282828]/10 min-h-[150px]"
                        data-testid="contact-message-input"
                        required
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-6 text-[11px] tracking-[0.15em] uppercase"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" />Send Message</>
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-[11px] text-[#676664]/40 mt-4 text-center">
                    We typically respond within 24-48 hours
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Link */}
      <section className="px-6 py-8 lg:px-16 text-center">
        <p className="text-[#3D2B1F]/60 text-sm">
          Looking for answers? Check our{" "}
          <a href="/faq" className="text-[#C4A4A0] hover:underline">
            FAQ
          </a>
        </p>
      </section>

      <Footer />
    </div>
  );
}
